'use client'

import SignUpForm from '@/components/forms/SignUpForm'
import { BasicModal } from '@/components/modals/BasicModal'
import { Button } from '@/components/ui/Button'
import UpgradeButton from '@/components/UpgradeButton'
import { sendGTMEvent } from '@next/third-parties/google'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { RiCloseLine } from 'react-icons/ri'

const STORY_LIMIT = 5
const RESET_DAYS = 30
const STORAGE_KEY_ARTICLES = 'pano_viewed_articles'
const STORAGE_KEY_FIRST_VIEW = 'pano_first_view_date'
const STORAGE_KEY_BANNER_DISMISSED = 'pano_paywall_banner_dismissed'

export default function Paywall({ userData, articleSlug }) {
  const router = useRouter()
  const [storiesViewed, setStoriesViewed] = useState(0)
  const [resetDate, setResetDate] = useState(null)
  const [loading, setLoading] = useState(true)
  const [hasTrackedPaywall, setHasTrackedPaywall] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(false)

  // Check if banner was dismissed this session
  useEffect(() => {
    const dismissed = sessionStorage.getItem(STORAGE_KEY_BANNER_DISMISSED)
    if (dismissed === 'true') {
      setBannerDismissed(true)
    }
  }, [])

  useEffect(() => {
    // Don't track for paying customers
    if (userData?.type === 'customer' || userData?.type === 'legacy_customer') {
      setLoading(false)
      return
    }

    // Don't track if no article slug
    if (!articleSlug) {
      setLoading(false)
      return
    }

    try {
      const now = new Date()

      // Get stored data
      const viewedArticlesStr = localStorage.getItem(STORAGE_KEY_ARTICLES)
      const firstViewStr = localStorage.getItem(STORAGE_KEY_FIRST_VIEW)

      let viewedArticles = viewedArticlesStr
        ? JSON.parse(viewedArticlesStr)
        : []
      let firstViewDate = firstViewStr ? new Date(firstViewStr) : null

      // Check if we need to reset (30 days have passed)
      if (firstViewDate) {
        const daysSinceFirst = Math.floor(
          (now - firstViewDate) / (1000 * 60 * 60 * 24)
        )

        if (daysSinceFirst >= RESET_DAYS) {
          // Reset everything
          viewedArticles = []
          firstViewDate = now
          localStorage.setItem(STORAGE_KEY_FIRST_VIEW, now.toISOString())
        }
      } else {
        // First ever view - set the start date
        firstViewDate = now
        localStorage.setItem(STORAGE_KEY_FIRST_VIEW, now.toISOString())
      }

      // Calculate reset date (30 days from first view)
      const calculatedResetDate = new Date(firstViewDate)
      calculatedResetDate.setDate(calculatedResetDate.getDate() + RESET_DAYS)

      // Track this article if not already viewed
      if (!viewedArticles.includes(articleSlug)) {
        viewedArticles.push(articleSlug)
        localStorage.setItem(
          STORAGE_KEY_ARTICLES,
          JSON.stringify(viewedArticles)
        )
      }

      setStoriesViewed(viewedArticles.length)
      setResetDate(calculatedResetDate)
    } catch (error) {
      console.error('Error tracking article view:', error)
      // Fail gracefully - don't show paywall if error
      setStoriesViewed(0)
    } finally {
      setLoading(false)
    }
  }, [articleSlug, userData?.type])

  // ✅ Track paywall_triggered when modal appears
  useEffect(() => {
    const hitLimit = storiesViewed >= STORY_LIMIT

    if (hitLimit && !hasTrackedPaywall && !loading) {
      sendGTMEvent({
        event: 'paywall_triggered',
      })

      setHasTrackedPaywall(true)
    }
  }, [storiesViewed, hasTrackedPaywall, loading, userData?.type])

  // Show nothing while loading
  if (loading) return null

  // Don't show paywall for paying customers
  if (userData?.type === 'customer' || userData?.type === 'legacy_customer') {
    return null
  }

  const remaining = Math.max(0, STORY_LIMIT - storiesViewed)
  const hitLimit = storiesViewed >= STORY_LIMIT
  const isGuest = userData?.type === 'guest'
  const showBanner = !hitLimit && remaining > 0 && !bannerDismissed

  const handleDismissBanner = () => {
    setBannerDismissed(true)
    sessionStorage.setItem(STORAGE_KEY_BANNER_DISMISSED, 'true')
  }

  // Format reset date
  const resetDateFormatted = resetDate
    ? resetDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : ''

  const handleClose = () => {
    router.push('/')
  }

  return (
    <>
      {/* Spacer to prevent content from being hidden behind fixed banner */}
      {showBanner && <div className="h-24 sm:h-20" />}

      {/* Bottom banner - show when user has remaining views */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-background text-foreground border-t border-border shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 flex-1">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center justify-center bg-foreground text-background text-xs font-bold rounded-full w-6 h-6">
                    {remaining}
                  </span>
                  <p className="font-serif text-sm font-medium">
                    {remaining === 1 ? 'article' : 'articles'} remaining
                  </p>
                </div>
                <Link
                  href="/subscribe"
                  className="hidden sm:block"
                  onClick={() => {
                    sendGTMEvent({
                      event: 'subscribe_button_clicked',
                      tracking_location: 'paywall_banner',
                    })
                  }}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap"
                  >
                    Subscribe
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-2">
                <Link
                  href="/subscribe"
                  className="sm:hidden"
                  onClick={() => {
                    sendGTMEvent({
                      event: 'subscribe_button_clicked',
                      tracking_location: 'paywall_banner',
                    })
                  }}
                >
                  <Button
                    variant="outline"
                    size="sm"
                    className="whitespace-nowrap text-xs px-3"
                  >
                    Subscribe
                  </Button>
                </Link>
                <button
                  onClick={handleDismissBanner}
                  className="p-1 hover:bg-muted rounded transition-colors"
                  aria-label="Dismiss banner"
                >
                  <RiCloseLine className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Paywall modal - show when limit reached */}
      <BasicModal isOpen={hitLimit} onClose={handleClose} className="max-w-lg">
        <div className="text-center space-y-6">
          <div>
            <h2 className="font-serif text-3xl font-bold text-foreground mb-3">
              Limit Reached
            </h2>
            <p className="font-serif text-base text-muted-foreground">
              You&apos;ve reached your limit of {STORY_LIMIT} free articles.
              {!isGuest && ` Your limit will reset on ${resetDateFormatted}.`}
            </p>
          </div>

          <div className="bg-green-50 dark:bg-green-950/20 border-2 border-green-200 dark:border-green-800 rounded-sm p-6">
            <p className="font-serif text-lg font-semibold text-foreground mb-2">
              Get Unlimited Access
            </p>
            <p className="font-serif text-sm text-muted-foreground">
              {isGuest
                ? 'Start with 5 free newsletter issues'
                : 'Subscribe for just $5/month'}
            </p>
          </div>

          {/* Guest: Show SignUpForm */}
          {isGuest && (
            <SignUpForm
              mode="signup"
              buttonLabel="Subscribe"
              onSuccess={handleClose}
              trackingLocation="paywall"
            />
          )}

          {/* Free User or Trial User: Show UpgradeButton */}
          {(userData?.type === 'free_user' ||
            userData?.type === 'trial_user') && (
            <UpgradeButton
              userData={userData}
              size="md"
              fullWidth
              trackingLocation="paywall"
            >
              Upgrade
            </UpgradeButton>
          )}

          <p className="font-serif text-xs text-muted-foreground">
            {isGuest ? (
              <>
                Already a subscriber?{' '}
                <Link href="/login" className="underline hover:text-foreground">
                  Log in
                </Link>
              </>
            ) : (
              <>
                Questions?{' '}
                <Link
                  href="/subscribe"
                  className="underline hover:text-foreground"
                >
                  Learn more
                </Link>
              </>
            )}
          </p>
        </div>
      </BasicModal>
    </>
  )
}
